/**
 * screenFit.js — сколько на экране места НА САМОМ ДЕЛЕ.
 *
 * Зачем. Оболочка мерилась в 100dvh. Это «динамическая высота окна» — величина,
 * которую браузер обещает пересчитывать при появлении и уходе своих панелей.
 * На деле обещание держится не везде и не всегда:
 *
 *   • старый WebView (и любой браузер без dvh) откатывается на 100vh, а vh —
 *     это ВЫСОТА БЕЗ ПАНЕЛЕЙ. Страница получается выше видимой области, и
 *     нижний ряд уезжает под адресную строку;
 *   • Safari на iOS пересчитывает dvh с задержкой и рывком: во время
 *     сворачивания панели вёрстка успевает отрисоваться по старой высоте;
 *   • в Chrome на Android панель прячется только при прокрутке, а у нас
 *     overflow:hidden — прокручивать нечего, и панель висит всегда.
 *
 * Отсюда и наблюдение: всё помещается только в полноэкранном режиме. Там
 * панелей браузера нет вовсе, и 100dvh случайно совпадает с истиной.
 *
 * Что делаем. Спрашиваем у браузера ВИДИМУЮ область напрямую —
 * window.visualViewport — и кладём её в --app-h/--app-w. Оболочка меряется
 * этими переменными; 100dvh остаётся запасным вариантом, если API нет.
 *
 * Две ловушки, из-за которых наивная реализация делает хуже:
 *
 *   1. Пинч-зум. При увеличении двумя пальцами visualViewport показывает
 *      ВИДИМЫЙ КУСОК страницы, а не окно: высота падает вдвое, и оболочка
 *      начала бы ужиматься вслед за пальцами. Смотрим scale и в этом случае
 *      не трогаем размер вовсе.
 *   2. Экранная клавиатура. Она съедает до половины экрана. Ужимать под неё
 *      всю программу нельзя: закроют клавиатуру — и все шесть окон пересчитают
 *      камеры дважды подряд. Признак клавиатуры — курсор в поле ввода плюс
 *      просевшая высота, а НЕ доля от window.innerHeight: первая версия
 *      сравнивала с ним, и проверка в настоящем браузере поймала осечку —
 *      при видимых 390 px innerHeight отдавал 844, высота «клавиатуры»
 *      совпала с обычным ландшафтом, и оболочка осталась вдвое выше экрана.
 */

/** Насколько должна просесть высота при фокусе в поле ввода, чтобы счесть это клавиатурой. */
const KEYBOARD_DROP_PX = 80;

/** Меньше этого порога изменения не трогаем вёрстку: незачем дёргать раскладку. */
const EPSILON_PX = 2;

/**
 * Чистый расчёт: что взять за размер оболочки.
 *
 * Возвращает h === null, когда размер трогать НЕЛЬЗЯ (идёт пинч-зум, вылезла
 * клавиатура). Это не «ошибка» — это «оставь как было».
 *
 * Почему не сравниваем с window.innerHeight. Он врёт в обе стороны: на
 * мобильных отдаёт высоту БЕЗ панелей браузера (из-за чего нижний ряд и уезжал
 * под адресную строку), а в режиме эмуляции устройства может отдать вообще
 * чужое число — проверено в браузере: при видимой области 390 px innerHeight
 * рапортовал 844. Единственный надёжный источник видимой области —
 * visualViewport; innerHeight остаётся только запасным, когда API нет вовсе.
 *
 * @param {object} p
 * @param {{width:number,height:number,scale:number}|null} p.vv visualViewport
 * @param {number} p.innerWidth
 * @param {number} p.innerHeight
 * @param {boolean} [p.editableFocused] курсор стоит в поле ввода
 * @param {number|null} [p.prevH] высота, применённая в прошлый раз
 * @returns {{w:number|null, h:number|null, source:string}}
 */
export function computeAppSize({
  vv, innerWidth, innerHeight, editableFocused = false, prevH = null,
  embed = null, appTop = 0,
}) {
  const w0 = Number.isFinite(innerWidth) && innerWidth > 0 ? Math.round(innerWidth) : 0;
  const h0 = Number.isFinite(innerHeight) && innerHeight > 0 ? Math.round(innerHeight) : 0;

  // [10.0.2] Программу встроили в чужую страницу, и её рамка нам доступна.
  //
  // Тогда мерить свою visualViewport бессмысленно: внутри рамки она равна
  // высоте самой рамки, а рамку хозяин страницы мог сделать любой — хоть
  // вдвое выше экрана. Именно так нижний ряд кнопок и оказывался за краем:
  // программа честно занимала всю рамку, а видно было только её верх.
  //
  // Берём то, что видно у РОДИТЕЛЯ, и подгоняем рамку под это (подгонка — в
  // вызывающем коде). После подгонки position:fixed внутри рамки снова
  // означает «у края экрана», как в отдельной вкладке.
  if (embed && embed.availH > 0 && embed.availW > 0) {
    return { w: Math.round(embed.availW), h: Math.round(embed.availH), source: 'embed' };
  }

  if (!vv || !(vv.height > 0) || !(vv.width > 0)) {
    return { w: w0, h: h0, source: 'window' };
  }

  const scale = Number.isFinite(vv.scale) && vv.scale > 0 ? vv.scale : 1;
  if (Math.abs(scale - 1) > 0.01) {
    // Пинч-зум: visualViewport показывает видимый КУСОК страницы, а не окно.
    // Пока пальцы на экране, размер оболочки менять незачем вовсе.
    return prevH ? { w: null, h: null, source: 'pinch' } : { w: w0, h: h0, source: 'pinch' };
  }

  if (editableFocused && Number.isFinite(prevH) && vv.height < prevH - KEYBOARD_DROP_PX) {
    // Клавиатура. Определяем по фокусу в поле ввода, а не по доле от
    // innerHeight: доля срабатывала вхолостую там, где innerHeight врёт.
    return { w: null, h: null, source: 'keyboard' };
  }

  // Программа может начинаться не от верхнего края: над ней бывает шапка
  // сайта, в который её вставили куском разметки (без рамки). Тогда доступной
  // высоты ровно на столько же меньше.
  //
  // Отступ больше половины экрана означает, что программу пролистали почти
  // целиком — подстраиваться под остаток бессмысленно, вернём полную высоту.
  const h = Math.round(vv.height);
  const off = Number.isFinite(appTop) && appTop > 0 ? Math.round(appTop) : 0;
  const usable = (off > 0 && off < h / 2) ? h - off : h;

  return {
    w: Math.round(vv.width),
    h: usable,
    source: off > 0 && usable !== h ? 'visualViewport+offset' : 'visualViewport',
  };
}

/** Стоит ли курсор в поле, из-за которого могла вылезти клавиатура. */
function _editableFocused() {
  try {
    const el = document.activeElement;
    if (!el) return false;
    if (el.isContentEditable) return true;
    const tag = (el.tagName || '').toLowerCase();
    if (tag === 'textarea') return true;
    if (tag !== 'input') return false;
    const type = (el.type || 'text').toLowerCase();
    return !['button', 'submit', 'reset', 'checkbox', 'radio', 'range', 'file', 'color', 'image'].includes(type);
  } catch (e) { return false; }
}

/**
 * Встроена ли программа в чужую страницу и достаём ли мы до неё.
 *
 * window.frameElement бросает исключение, если страница-хозяин с другого
 * домена; это и есть проверка «свой источник или чужой».
 */
function _embedInfo() {
  if (typeof window === 'undefined' || window.self === window.top) {
    return { embedded: false, sameOrigin: false };
  }
  try {
    const frame = window.frameElement;
    const parentWin = window.parent;
    if (!frame || !parentWin) return { embedded: true, sameOrigin: false };
    // Обращение к родителю с чужого домена бросит здесь.
    const pvv = parentWin.visualViewport;
    const visH = Math.round(pvv?.height || parentWin.innerHeight || 0);
    const visW = Math.round(pvv?.width || parentWin.innerWidth || 0);
    if (!(visH > 0) || !(visW > 0)) return { embedded: true, sameOrigin: false };

    // Рамка начинается не от верха экрана: над ней шапка сайта. Значит и
    // места у неё не «весь экран», а «экран минус то, что выше».
    //
    // Без этой поправки рамка получала полную высоту экрана, её низ уезжал
    // ровно на высоту шапки, и нижний ряд кнопок оказывался за краем — то же
    // самое, что было до подгонки, только на меньшую величину.
    const rect = frame.getBoundingClientRect();
    const visTop = Math.round(pvv?.offsetTop || 0);
    const top = Math.max(Math.round(rect.top), visTop);
    let availH = visH + visTop - top;
    // Совсем крохотный остаток — значит рамку пролистали почти целиком;
    // ужимать программу до полоски незачем.
    if (availH < 240) availH = Math.min(visH, 240);
    const availW = Math.min(visW, Math.round(rect.width) || visW);
    if (!(availH > 0) || !(availW > 0)) return { embedded: true, sameOrigin: false };
    return { embedded: true, sameOrigin: true, frame, availH, availW, frameTop: top };
  } catch (e) {
    return { embedded: true, sameOrigin: false };
  }
}

/** Насколько верх программы опущен относительно верха видимой области. */
function _appTopOffset() {
  try {
    const app = document.getElementById('app') || document.body;
    if (!app) return 0;
    const top = app.getBoundingClientRect().top;
    const vvTop = window.visualViewport?.offsetTop || 0;
    return Math.max(0, Math.round(top - vvTop));
  } catch (e) { return 0; }
}

let _applied = { w: 0, h: 0, source: '' };
let _raf = 0;
let _detach = null;
let _askedParent = 0;

function _apply() {
  _raf = 0;
  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  const emb = _embedInfo();

  // Рамку подгоняем ДО замера: после неё наша собственная visualViewport уже
  // будет равна видимой области, и всё сойдётся само.
  if (emb.embedded && emb.sameOrigin) {
    try {
      if (Math.abs((emb.frame.clientHeight || 0) - emb.availH) > EPSILON_PX) {
        emb.frame.style.height = `${emb.availH}px`;
        emb.frame.style.maxHeight = `${emb.availH}px`;
      }
    } catch (e) { /* хозяин страницы запретил — обойдёмся своими силами */ }
  } else if (emb.embedded) {
    // Чужой домен: сами дотянуться не можем, но можем попросить. Хозяин
    // страницы слушает сообщение и ставит высоту сам (см. README).
    const now = Date.now();
    if (now - _askedParent > 1000) {
      _askedParent = now;
      try {
        window.parent.postMessage({ type: 'mikhmel:fit-request', height: vv?.height || 0 }, '*');
      } catch (e) { /* */ }
    }
  }

  const size = computeAppSize({
    vv: vv ? { width: vv.width, height: vv.height, scale: vv.scale } : null,
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    editableFocused: _editableFocused(),
    prevH: _applied.h || null,
    embed: (emb.embedded && emb.sameOrigin) ? { availH: emb.availH, availW: emb.availW } : null,
    appTop: emb.embedded ? 0 : _appTopOffset(),
  });
  if (!size.h || !size.w) return;   // «оставь как было» либо мерить нечем
  if (Math.abs(size.h - _applied.h) < EPSILON_PX && Math.abs(size.w - _applied.w) < EPSILON_PX) return;

  _applied = { w: size.w, h: size.h, source: size.source };
  const root = document.documentElement;
  root.style.setProperty('--app-h', `${size.h}px`);
  root.style.setProperty('--app-w', `${size.w}px`);

  // Кому нужно — пересчитает свои холсты. Вьюпорты слушают ResizeObserver на
  // контейнере и узнают об этом сами; событие здесь для всего остального.
  try {
    window.dispatchEvent(new CustomEvent('mikhmel:app-size', { detail: { ...size } }));
  } catch (e) { /* */ }
}

function _schedule() {
  if (_raf) return;
  _raf = requestAnimationFrame(_apply);
}

/**
 * Включить измерение. Идемпотентно: повторный вызов переустанавливает
 * слушателей, а не добавляет вторых.
 */
export function initScreenFit() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => {};
  destroyScreenFit();

  const ac = new AbortController();
  const opts = { passive: true, signal: ac.signal };

  window.addEventListener('resize', _schedule, opts);
  window.addEventListener('orientationchange', _schedule, opts);

  const vv = window.visualViewport;
  if (vv) {
    vv.addEventListener('resize', _schedule, opts);
    // Прокрутка видимой области — это и есть сворачивание панели браузера.
    vv.addEventListener('scroll', _schedule, opts);
  }

  // Полноэкранный режим меняет высоту без события resize в части браузеров.
  document.addEventListener('fullscreenchange', _schedule, opts);
  document.addEventListener('webkitfullscreenchange', _schedule, opts);

  // [10.0.2] Встроены в свою же страницу — слушаем и её.
  //
  // Панель браузера сворачивается у РОДИТЕЛЯ, и внутри рамки об этом никто не
  // сообщает: наша собственная visualViewport не меняется, пока не изменится
  // сама рамка. Без этих слушателей подгонка срабатывала бы только при
  // повороте экрана.
  let tick = 0;
  const emb0 = _embedInfo();
  if (emb0.embedded && emb0.sameOrigin) {
    try {
      const pw = window.parent;
      pw.addEventListener('resize', _schedule, opts);
      pw.visualViewport?.addEventListener('resize', _schedule, opts);
      pw.visualViewport?.addEventListener('scroll', _schedule, opts);
      // Подстраховка: адресную строку прячут и без событий, доступных ребёнку.
      tick = setInterval(_schedule, 1000);
    } catch (e) { /* хозяин страницы закрылся */ }
  }

  _detach = () => {
    ac.abort();
    if (tick) { clearInterval(tick); tick = 0; }
    if (_raf) { cancelAnimationFrame(_raf); _raf = 0; }
  };

  _apply();
  // Панели браузера доезжают уже после первого кадра — перемеряем.
  setTimeout(_schedule, 300);
  return destroyScreenFit;
}

export function destroyScreenFit() {
  if (_detach) { try { _detach(); } catch (e) { /* */ } _detach = null; }
  _applied = { w: 0, h: 0, source: '' };
}

/** Встроена ли программа в чужую страницу — для отчёта о состоянии. */
export function getEmbedState() {
  const e = _embedInfo();
  return { embedded: !!e.embedded, sameOrigin: !!e.sameOrigin, availH: e.availH || null };
}

/** Последний применённый размер — для отладки и проверок. */
export function getAppSize() { return { ..._applied }; }

export default { initScreenFit, destroyScreenFit, computeAppSize, getAppSize, getEmbedState };
